import { useSelector } from 'react-redux';
import { selectors } from 'darwin_core';
import { MODES } from 'darwin_core/utils/consts.js';

import { useZoneControls } from '../controls/useZoneControls.js';
import { sendBlockedReason } from '../controls/useControlsSendable.js';
import Radial from './Radial.jsx';

// The setpoint dial wired to darwin_core for one heating zone. It reads the
// reported/optimistic state and threads the Radial's intent through
// useZoneControls' setSetpoint dispatcher (which owns the real clamp and the
// Auto → OverrideNext transition; the dial pre-snaps to the 50-centidegree grid).
//
// Disabled — non-interactive but still showing the last setpoint — when its
// thermostat is in holiday, the zone is Off (its setpoint is the frost floor and
// darwin_core's setSetpoint would no-op), or our socket is down. Hot-water zones
// have no user setpoint, so the zone screen renders this only for heating zones.
//
// OFFLINE is not that: an unreachable zone gets the Radial's own offline face,
// which drops the arc and the readout for a single "Offline" (the app's dial does
// the same). So it is passed as its own flag, not as a disabled reason, and it
// wins over the socket's — a device the cloud says is down stays down whatever our
// own connection is doing, and the ConnectionBanner above every screen speaks for
// that.
export default function SetpointDial({ homeId, zoneId }) {
  const { online, inHoliday, sendable, connectionStatus, actions } =
    useZoneControls(homeId, zoneId);

  const setpoint = useSelector((s) => selectors.getSetpoint(s, homeId, zoneId));
  const roomTemperature = useSelector((s) =>
    selectors.getCurrentTemperature(s, homeId, zoneId),
  );
  const minSetpoint = useSelector((s) =>
    selectors.getMinSetpoint(s, homeId, zoneId),
  );
  const maxSetpoint = useSelector((s) =>
    selectors.getMaxSetpoint(s, homeId, zoneId),
  );
  const mode = useSelector((s) => selectors.getMode(s, homeId, zoneId));

  const isOff = mode === MODES.Off;
  const hasSetpoint = typeof setpoint === 'number';
  const disabled = !sendable || !online || inHoliday || isOff || !hasSetpoint;

  // The sub-line reason, for the states that still show a setpoint. Connection
  // first: while the socket is down a change could not be sent at all, so that is
  // the reason to show over any zone-specific one. Offline has no reason line —
  // it has a face.
  let disabledReason;
  if (!sendable) {
    disabledReason = sendBlockedReason(connectionStatus);
  } else if (inHoliday) {
    disabledReason = 'On holiday';
  } else if (isOff) {
    disabledReason = 'Off';
  }

  return (
    <Radial
      // Fall back to the min so the dial still renders a sensible face when the
      // zone reports no setpoint (offline); it is disabled in that case anyway.
      setpoint={hasSetpoint ? setpoint : (minSetpoint ?? 500)}
      roomTemperature={roomTemperature}
      minSetpoint={minSetpoint}
      maxSetpoint={maxSetpoint}
      disabled={disabled}
      disabledReason={disabledReason}
      offline={!online}
      onSetpointChange={actions.setSetpoint}
    />
  );
}
